import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, ChevronRight, Clock, MessageSquare, Archive } from "lucide-react";
import { format } from "date-fns";
import AdminTicketDetail from "../SupportTickets/AdminTicketDetail";

const PAGE_SIZE = 25;
const STATUS_COLORS = {
    OPEN: "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-yellow-100 text-yellow-700",
    WAITING_ON_CUSTOMER: "bg-purple-100 text-purple-700",
    RESOLVED: "bg-green-100 text-green-700",
    CLOSED: "bg-gray-100 text-gray-700",
};
const PRIORITY_COLORS = {
    LOW: "bg-gray-100 text-gray-600",
    NORMAL: "bg-blue-100 text-blue-600",
    HIGH: "bg-orange-100 text-orange-600",
    URGENT: "bg-red-100 text-red-600",
};

export default function TicketsTab({ companyId }) {
    const { t } = useTranslation();
    const [tickets, setTickets] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [showArchived, setShowArchived] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const { call: fetchTickets, isLoading } = useBluelagoon({ method: "getAdminTickets" });

    const load = useCallback(() => {
        fetchTickets({
            companyId,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
            includeArchived: showArchived || undefined,
        })
            .then((res) => {
                setTickets(res?.data?.tickets || []);
                setTotal(res?.data?.pagination?.total || 0);
            })
            .catch(() => setTickets([]));
    }, [fetchTickets, companyId, page, showArchived]);

    useEffect(() => { load(); }, [load]);

    if (selectedId) {
        return (
            <AdminTicketDetail
                ticketId={selectedId}
                onBack={() => setSelectedId(null)}
                onNavigate={(id) => setSelectedId(id)}
                ticketIds={tickets.map((tk) => tk.id)}
            />
        );
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Button size="sm" variant={showArchived ? "default" : "outline"} onClick={() => { setShowArchived((v) => !v); setPage(1); }}>
                    <Archive className="h-3 w-3 mr-1" />
                    {showArchived ? t("admin.manageCompany.tickets.hideArchived", "Hide archived") : t("admin.manageCompany.tickets.showArchived", "Show archived")}
                </Button>
                <span className="text-xs text-muted-foreground">{t("admin.manageCompany.tickets.total", "{{count}} tickets", { count: total })}</span>
            </div>
            {isLoading && tickets.length === 0 ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : tickets.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 text-sm">{t("admin.manageCompany.tickets.empty", "No tickets")}</div>
            ) : (
                <div className="space-y-2">
                    {tickets.map((tk) => (
                        <Card key={tk.id} className="p-3 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedId(tk.id)}>
                            <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                        <span className="text-xs text-muted-foreground font-mono">#{tk.id.slice(0, 8)}</span>
                                        {tk.archivedAt && <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-[10px]"><Archive className="w-3 h-3 mr-0.5" />Archived</Badge>}
                                        <Badge className={`${STATUS_COLORS[tk.status] || STATUS_COLORS.OPEN} text-[10px]`}>{tk.status?.replace(/_/g, " ")}</Badge>
                                        <Badge variant="outline" className={`${PRIORITY_COLORS[tk.priority] || PRIORITY_COLORS.NORMAL} text-[10px]`}>{tk.priority}</Badge>
                                    </div>
                                    <h3 className="font-medium truncate text-sm">{tk.subject}</h3>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{format(new Date(tk.createdAt), "MMM d, yyyy")}</span>
                                        {tk.messageCount > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{tk.messageCount}</span>}
                                        {tk.createdByEmail && <span className="font-mono truncate max-w-[200px]">{tk.createdByEmail}</span>}
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                            </div>
                        </Card>
                    ))}
                </div>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs">
                    <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t("common.previous", "Previous")}</Button>
                    <span className="text-muted-foreground">{t("common.pageOf", "Page {{page}} of {{total}}", { page, total: totalPages })}</span>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t("common.next", "Next")}</Button>
                </div>
            )}
        </div>
    );
}
